import { Box, Fade, Stack } from '@mui/material'
import pxToRem from '../../../../helpers/pxToRem'
import { RegModalWrapper } from '../ModalContent'
import styles from '../index.module.scss'
import { useState } from 'react'
import samplePhoto from '../../../../assets/images/teacher-registration/sample-photo-holding-id.png'
import { useDispatch, useSelector } from 'react-redux'
import { UserUpdate } from '../../../../redux/userRegistrationSlice'
import PhotoUpload from '../../../../components/FileUpload/PhotoUpload'
import { FileType } from '../../../../components/FileUpload'
import { documentUploadHandler } from './helper'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { RegFileUploadLabel } from '../../../../components/FileUpload/RegUploadComponents'
import { selectUserUid } from '../../../../redux/userSlice'
import { selectDerivedIdentityState } from '../../../../redux/teacherSlice'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import { IdentificationDocument } from '../../../../schemas/teacherReg'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { FileUploadSchema } from '../../../../schemas/helper'
import { zodResolver } from '@hookform/resolvers/zod'
import { push, ref, update } from 'firebase/database'
import { db } from '../../../../firebase/configs'

const formSchema = z.object({
    photo: FileUploadSchema,
})

export const PhotoHoldingIDWrapper = () => {
    const identityDocs = useSelector(selectDerivedIdentityState)

    if (identityDocs.loading) return <LoadingSpinner />

    return <PhotoHoldingID {...identityDocs.values.photoHoldingId} />
}

const PhotoHoldingID = ({
    id,
    data,
}: {
    id: string | undefined
    data: IdentificationDocument
}) => {
    const dispatch = useDispatch()
    const userId = useSelector(selectUserUid)

    const {
        setValue,
        formState: { isValid, isDirty },
    } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            photo: data.value || undefined,
        },
    })

    const [file, setFile] = useState<FileType | undefined>(
        typeof data.value === 'string' ? { file: data.value } : undefined
    )
    const onFileChange = (file: any) => {
        let f =
            file instanceof File
                ? file
                : new File([file], file.name, { type: file.type })

        setFile({ file: file, fileName: file?.name })
        setValue('photo', f, {
            shouldDirty: true,
            shouldValidate: true,
        })
    }

    const handleUpdate = async () => {
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }
            if (!isDirty) return
            let updateData: UserUpdate = {}

            const { fileUrl, fileType } = await documentUploadHandler(
                userId,
                file,
                {
                    url: data.value,
                    type: data.type,
                }
            ) // TODO: Add type and name here

            const toUpdate: IdentificationDocument = {
                documentType: 'photoHolidingIdentification',
                type: fileType,
                value: fileUrl,
                state: 'submitted',
                country: null,
                name: null,
                birthDate: null,
                reference: null,
                docTitle: null,
            }

            if (typeof id === 'string') {
                // Update data
                await update(
                    ref(db, `teachers/${userId}/identity/${id}`),
                    toUpdate
                )
            } else {
                await push(ref(db, `teachers/${userId}/identity`), toUpdate)
            }
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    return (
        <>
            <RegModalWrapper
                title="Identity Verification - Photo Holding ID"
                handleUpdate={handleUpdate}
                state={data.state}
                isAllComplete={isValid}
            >
                <Stack gap={pxToRem(40)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>Photo Holding ID</h5>
                        <p className={styles.p}>
                            Please upload a photo of yourself holding one of
                            your identity documents
                        </p>
                    </Stack>
                    <Stack className={styles.profileContainer}>
                        <Box className={styles['profilePhoto--left']}>
                            <PhotoUpload
                                file={file}
                                onChangeCallback={onFileChange}
                            />
                            {/*  TODO: Need to alter the text here for UX */}
                            <Fade in={file !== undefined}>
                                <div>
                                    <RegFileUploadLabel
                                        file={file}
                                        reset={() => setFile(undefined)}
                                        text={<small></small>}
                                    />
                                </div>
                            </Fade>
                        </Box>
                        <Stack className={styles['profilePhoto--right2']}>
                            <small
                                style={{
                                    alignSelf: 'flex-end',
                                    color: styles.shades100,
                                }}
                            >
                                Example*
                            </small>
                            <img src={samplePhoto} alt={'Holding ID sample'} />
                        </Stack>
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export default PhotoHoldingID
